import {
  AssignmentReturnOutlined,
  BlockOutlined,
  CancelOutlined,
  ConfirmationNumberOutlined,
  CurrencyExchange,
  DeliveryDiningOutlined,
  DoneAll,
  ErrorOutline,
  HourglassEmpty,
  Inventory2Outlined,
  KeyboardReturnOutlined,
  LocalShippingOutlined,
  NoteAddOutlined,
  PauseCircleOutlined,
  SvgIconComponent,
  SyncOutlined,
  TaskAlt,
  TwoWheelerOutlined,
  ViewModuleOutlined,
} from "@mui/icons-material";
import { Box, Tab, Tabs } from "@mui/material";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import { swayyColors, swayyFont } from "../../theme/tokens";

interface SwayyTabIcon {
  Icon: SvgIconComponent;
  color: string;
}

interface SwayyStatusTabsProps {
  value: string;
  onChange: (statusId: string) => void;
  tabs: FilterValue[];
  icons: Record<string, SwayyTabIcon>;
}

export const SWAYY_ORDER_STATUS_TAB_ICONS: Record<string, SwayyTabIcon> = {
  all: { Icon: ViewModuleOutlined, color: swayyColors.textSecondary },
  pending_payment: { Icon: HourglassEmpty, color: swayyColors.warning },
  confirmed: { Icon: TaskAlt, color: swayyColors.success },
  processing: { Icon: SyncOutlined, color: swayyColors.pine },
  partially_shipped: { Icon: Inventory2Outlined, color: swayyColors.pine },
  shipped: { Icon: LocalShippingOutlined, color: swayyColors.success },
  out_for_delivery: {
    Icon: DeliveryDiningOutlined,
    color: swayyColors.success,
  },
  delivered: { Icon: DoneAll, color: swayyColors.success },
  cancelled: { Icon: CancelOutlined, color: swayyColors.danger },
  refunded: { Icon: CurrencyExchange, color: swayyColors.danger },
};

export const SWAYY_SHIPMENT_STATUS_TAB_ICONS: Record<string, SwayyTabIcon> = {
  all: { Icon: ViewModuleOutlined, color: swayyColors.textSecondary },
  created: { Icon: NoteAddOutlined, color: swayyColors.warning },
  picked_up: { Icon: Inventory2Outlined, color: swayyColors.pine },
  in_transit: { Icon: LocalShippingOutlined, color: swayyColors.pine },
  out_for_delivery: { Icon: TwoWheelerOutlined, color: swayyColors.pine },
  delivered: { Icon: DoneAll, color: swayyColors.success },
  failed_delivery: { Icon: ErrorOutline, color: swayyColors.danger },
  rto_initiated: { Icon: KeyboardReturnOutlined, color: swayyColors.danger },
  rto_delivered: { Icon: AssignmentReturnOutlined, color: swayyColors.danger },
};

export const SWAYY_COUPON_STATUS_TAB_ICONS: Record<string, SwayyTabIcon> = {
  all: { Icon: ViewModuleOutlined, color: swayyColors.textSecondary },
  active: { Icon: ConfirmationNumberOutlined, color: swayyColors.success },
  inactive: { Icon: PauseCircleOutlined, color: swayyColors.textSecondary },
  expired: { Icon: ErrorOutline, color: swayyColors.danger },
  exhausted: { Icon: BlockOutlined, color: swayyColors.danger },
};

const SwayyStatusTabs = ({
  value,
  onChange,
  tabs,
  icons,
}: SwayyStatusTabsProps) => (
  <Box
    sx={{
      borderBottom: `1px solid ${swayyColors.border}`,
      mb: 2,
    }}
  >
    <Tabs
      value={value}
      onChange={(_event, statusId: string) => onChange(statusId)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={{
        minHeight: 48,
        "& .MuiTabs-indicator": {
          height: 3,
          borderRadius: "3px 3px 0 0",
          backgroundColor: swayyColors.pine,
        },
        "& .MuiTab-root": {
          minHeight: 48,
          minWidth: "auto",
          px: { xs: 1.25, sm: 1.75 },
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontFamily: swayyFont,
          fontWeight: 600,
          fontSize: { xs: 11, sm: 12 },
          color: swayyColors.textSecondary,
          whiteSpace: "nowrap",
        },
        "& .MuiTab-iconWrapper": { mr: 0.75, mb: 0 },
        "& .Mui-selected": {
          color: `${swayyColors.deepGreen} !important`,
        },
      }}
    >
      {tabs.map((tab) => {
        const tabIcon = icons[tab.id];
        const Icon = tabIcon?.Icon;
        return (
          <Tab
            key={tab.id}
            value={tab.id}
            disableRipple
            iconPosition="start"
            icon={
              Icon ? (
                <Icon
                  sx={{
                    fontSize: { xs: 16, sm: 18 },
                    color: `${tabIcon.color} !important`,
                  }}
                />
              ) : undefined
            }
            label={tab.value.toUpperCase()}
          />
        );
      })}
    </Tabs>
  </Box>
);

export default SwayyStatusTabs;
